import { Descriptions, Tabs, Tag, Typography } from "antd";
import React from "react";
import styled from "styled-components";

import { RunHistoryPanel } from "./RunHistoryPanel";
import { ThreadPanel } from "./ThreadPanel";
import { AlertItem } from "./types";

const { Text } = Typography;

const PanelWrapper = styled.div`
  background: #fafafa;
  border-radius: 6px;
  padding: 12px 16px;
`;

const PromptBlock = styled.pre`
  margin: 0;
  max-height: 160px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  color: #444;
`;

interface AlertDebugPanelProps {
  alert: AlertItem;
}

function sourceLabel(source?: string): string {
  if (source === "saved_preview") {
    return "Saved preview";
  }
  if (source === "last_successful_run") {
    return "Latest successful run";
  }
  return "None";
}

function sourceColor(source?: string): string {
  if (source === "saved_preview") {
    return "green";
  }
  if (source === "last_successful_run") {
    return "blue";
  }
  return "default";
}

function renderPrompt(prompt?: string): React.ReactNode {
  if (!prompt) {
    return <Text type="secondary">—</Text>;
  }
  return <PromptBlock>{prompt}</PromptBlock>;
}

function ConfigPanel({ alert }: AlertDebugPanelProps) {
  return (
    <PanelWrapper>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Trigger prompt">
          {renderPrompt(alert.trigger_prompt)}
        </Descriptions.Item>
        <Descriptions.Item label="Content prompt">
          {renderPrompt(alert.content_prompt)}
        </Descriptions.Item>
        <Descriptions.Item label="Presentation prompt">
          {renderPrompt(alert.presentation_prompt)}
        </Descriptions.Item>
        <Descriptions.Item label="Granularity">{alert.granularity || "summary"}</Descriptions.Item>
        <Descriptions.Item label="Creativity">{alert.creativity_level || "—"}</Descriptions.Item>
        <Descriptions.Item label="Image orientation">
          {alert.image_orientation || "landscape"}
        </Descriptions.Item>
      </Descriptions>
    </PanelWrapper>
  );
}

function StylePanel({ alert }: AlertDebugPanelProps) {
  return (
    <PanelWrapper>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Real-run style source">
          <Tag color={sourceColor(alert.style_reference_source)}>
            {sourceLabel(alert.style_reference_source)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Saved preview image">
          {alert.last_generated_image_url ? (
            <Text copyable={{ text: alert.last_generated_image_url }}>
              {alert.last_generated_image_url}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Saved preview prompt">
          {renderPrompt(alert.last_generated_image_prompt)}
        </Descriptions.Item>
        <Descriptions.Item label="Latest successful run image">
          {alert.last_successful_run_image_url ? (
            <Text copyable={{ text: alert.last_successful_run_image_url }}>
              {alert.last_successful_run_image_url}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Latest successful run prompt">
          {renderPrompt(alert.last_successful_run_image_prompt)}
        </Descriptions.Item>
      </Descriptions>
    </PanelWrapper>
  );
}

export const AlertDebugPanel: React.FC<AlertDebugPanelProps> = ({ alert }) => {
  const tabItems = [
    {
      key: "runs",
      label: "Runs",
      children: <RunHistoryPanel alert={alert} />,
    },
    {
      key: "thread",
      label: "Thread",
      children: <ThreadPanel alertId={alert.smart_alert_id} />,
    },
    {
      key: "config",
      label: "Config",
      children: <ConfigPanel alert={alert} />,
    },
    {
      key: "style",
      label: "Style",
      children: <StylePanel alert={alert} />,
    },
  ];

  return <Tabs size="small" defaultActiveKey="runs" items={tabItems} />;
};
